import { useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PageTitle } from "../../PageTitle/PageTitle";
import './QuestionnaireSessionPage.scss'
import { I18nReactContext } from "../../../contexts/I18nContext";
import { QuestionnaireSessionFrame } from "../QuestionnaireSessionFrame/QuestionnaireSessionFrame";
import { QuestionnaireQuestionsNavigation } from "../QuestionnaireQuestionsNavigation/QuestionnaireQuestionsNavigation";
import {
    QuestionnaireSessionReactContext,
    QuestionnaireSessionReactContextProvider
} from "../../../contexts/QuestionnaireContext/QuestionnaireSessionContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { Modal } from "../../Smart/Modal/Modal";
import { Button } from "../../Form/Button/Button";
import { buttonColors } from "../../Pickers/InputTheme";
import { get } from "lodash";
import { useContextSelector } from "use-context-selector";
import { QuestionnaireInstructionModal } from "./SessionModals/QuestionnaireInstructionModal/QuestionnaireInstructionModal";
import { QuestionnaireSuccessModal } from "./SessionModals/QuestionnaireSuccessModal/QuestionnaireSuccessModal";
import { QuestionnaireFailureModal } from "./SessionModals/QuestionnaireFailureModal/QuestionnaireFailureModal";
import { SignDocTypes } from "../../SignDoc/SignDocLib";
import { SurveyInstructionModal } from "./SessionModals/SurveyInstructionModal/SurveyInstructionModal";
import useSignDocTypes from "../../../Hooks/useSignDocTypes";

export const QuestionnaireSessionPage = () => {
    const { signDocId } = useParams();
    const initQuestionnaireSession = useContextSelector(QuestionnaireSessionReactContext, context => context.initQuestionnaireSession);
    const sessionQuestions = useContextSelector(QuestionnaireSessionReactContext, context => context.sessionQuestions);
    const questionnaireSession = useContextSelector(QuestionnaireSessionReactContext, context => context.questionnaireSession);
    const finishSessionFlag = useContextSelector(QuestionnaireSessionReactContext, context => context.finishSessionFlag);
    const finishSession = useContextSelector(QuestionnaireSessionReactContext, context => context.finishSession);
    const cancelFinishExam = useContextSelector(QuestionnaireSessionReactContext, context => context.cancelFinishExam);
    const currentQuestionIndex = useContextSelector(QuestionnaireSessionReactContext, context => context.currentQuestionIndex);
    const handlePreviousQuestion = useContextSelector(QuestionnaireSessionReactContext, context => context.handlePreviousQuestion);
    const handleNextQuestion = useContextSelector(QuestionnaireSessionReactContext, context => context.handleNextQuestion);
    const { i18n } = useContext(I18nReactContext);
    const { getNestedTranslation, getNestedProperty } = useSignDocTypes();
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const [finishQuestionnaire, setFinishQuestionnaire] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showFailureModal, setShowFailureModal] = useState(false);
    const [showInstructionModal, setShowInstructionModal] = useState(true);
    const [questionsLoaded, setQuestionsLoaded] = useState(false);
    const [learningPathRootId, setLearningPathRootId] = useState(null);

    // Detect if this is a survey
    const questionnaireType = get(questionnaireSession, 'type');

    useEffect(() => {
        try {
            const mappingJson = window.localStorage.getItem('signDocToLearningPathMap');
            if (mappingJson) {
                const mapping = JSON.parse(mappingJson);
                const learningPathId = mapping[signDocId];
                if (learningPathId) {
                    setLearningPathRootId(learningPathId);
                }
            }
        } catch (error) {
            console.error('Error reading learning path mapping from localStorage', error);
        }
    }, [signDocId]);

    const init = async () => {
        setIsLoading(true);
        try {
            await initQuestionnaireSession({ signDocId });
            setIsLoading(false);
            setQuestionsLoaded(true);
        } catch (error) {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        init();
    }, []);

    const handleSubmit = async () => {
        let newSessionWithScore = await finishSession();

        if (questionnaireType === SignDocTypes.SURVEY) {
            // For surveys, always show success
            setShowSuccessModal(true);
        } else if (questionnaireType === SignDocTypes.EXAM) {
            let score = get(newSessionWithScore, 'questionnaireSession.score', '0');
            let passGrade = get(newSessionWithScore, 'questionnaireSession.passGrade', '0');

            // For exams, check pass/fail
            if (parseFloat(score) >= parseFloat(passGrade)) {
                setShowConfetti(true);
                setShowSuccessModal(true);
            } else {
                setShowFailureModal(true);
            }
        }

        setFinishQuestionnaire(true);
        cancelFinishExam();
    }

    const createFinishModalText = useMemo(() => {
        if (questionnaireType === SignDocTypes.SURVEY) {
            // For surveys, allow finishing with unanswered questions
            return getNestedTranslation({
                type: questionnaireType,
                section: 'questionnaireSession',
                property: 'finishModalTextKey',
                defaultValue: i18n.translate('surveySession.modal.finishSurvey')
            });
        } else if (questionnaireType === SignDocTypes.EXAM) {
            // For exams, show different message based on completion
            let isAllQuestionsAnswered = sessionQuestions.every(question => {
                return Array.isArray(question.userAnswers) && question.userAnswers.length > 0;
            });
            return getNestedTranslation({
                type: questionnaireType,
                section: 'questionnaireSession',
                property: isAllQuestionsAnswered ? 'finishModalTextKey' : 'finishModalTextWithUnansweredKey',
            });
        }
    }, [questionnaireType, sessionQuestions, getNestedTranslation, i18n]);

    useEffect(() => {
        if (finishQuestionnaire) {
            const timeoutId = setTimeout(() => {
                const target = learningPathRootId
                    ? `/learningPathViewer/${learningPathRootId}`
                    : '/home';
                navigate(target, { replace: true });
            }, showConfetti ? 5000 : 3000);

            return () => clearTimeout(timeoutId);
        }
    }, [finishQuestionnaire, showConfetti, learningPathRootId]);

    // Defines the navigation logic to return to the associated learning path, or to the home page if no learning path is linked.
    const navigateBack = useCallback(() => {
        const target = learningPathRootId
            ? `/learningPathViewer/${learningPathRootId}`
            : '/home';
        navigate(target, { replace: true });
    }, [learningPathRootId]);

    const handleStartSession = () => {
        setShowInstructionModal(false);
    };

    const closeSuccessModal = () => {
        setShowSuccessModal(false);
        navigateBack();
    };

    const closeFailureModal = () => {
        setShowFailureModal(false);
        navigateBack();
    };

    const finishModalTitle = useMemo(() => {
        return getNestedTranslation({
            type: questionnaireType,
            section: 'questionnaireSession',
            property: 'finishModalTitleKey',
        });
    }, [questionnaireType, getNestedTranslation, i18n]);

    const closeButtonText = useMemo(() => {
        return getNestedTranslation({
            type: questionnaireType,
            section: 'questionnaireSession',
            property: 'closeButtonTextKey',
        });
    }, [questionnaireType, getNestedTranslation, i18n]);

    const successMessage = getNestedTranslation({
        type: questionnaireType,
        section: 'questionnaireSession',
        property: 'successMessageKey',
    });

    const failureMessage = getNestedTranslation({
        type: questionnaireType,
        section: 'questionnaireSession',
        property: 'failureMessageKey',
    });

    const nextButtonText = useMemo(() => {
        const isLastQuestion = currentQuestionIndex + 1 === get(sessionQuestions, 'length', 0);

        return getNestedTranslation({
            type: questionnaireType,
            section: 'questionnaireSession',
            property: isLastQuestion ? 'finishButtonTextKey' : 'nextButtonTextKey',
            defaultValue: i18n.translate('questionnaireSession.buttons.nextQuestion')
        });
    }, [currentQuestionIndex, sessionQuestions, questionnaireType, getNestedTranslation, i18n]);

    return (
        <div className="questionnaireSessionPage MenuPage">
            <PageTitle
                size="minimized"
                title={get(questionnaireSession, 'questionnaireSession.title')}
                subTitle=""
                subTitleBold=""
                customComponent=""
                noLogo={true}
            />
            <div className="menuInnerPage">
                {!isLoading && !showInstructionModal ?
                    !finishQuestionnaire && (
                        <div className="questionnaireSessionContainer">
                            <QuestionnaireQuestionsNavigation />
                            <QuestionnaireSessionFrame />
                            <div className="questionnaireSessionNavigation">
                                <Button
                                    color={buttonColors.GOLD}
                                    disabled={currentQuestionIndex === 0}
                                    onClick={handlePreviousQuestion}
                                    text={String(i18n.translate('questionnaireSession.buttons.previousQuestion'))}
                                    dataTestId="previous-question-button"
                                />
                                <Button
                                    color={buttonColors.GOLD}
                                    onClick={handleNextQuestion}
                                    text={String(nextButtonText)}
                                    dataTestId="next-question-button"
                                />
                            </div>
                        </div>
                    ) : <LoadingSpinner />
                }

                <Modal
                    open={finishSessionFlag}
                    onClose={cancelFinishExam}>
                    <div className="modalWrapper">
                        <div className="modalTitle">
                            <div>{finishModalTitle}</div>
                        </div>
                        <div className="modalContent">
                            {createFinishModalText}
                        </div>
                        <div className="modalFooter">
                            <Button
                                color={buttonColors.YELLOW}
                                onClick={cancelFinishExam}
                                text={String(closeButtonText)}
                            />
                            <Button
                                color={buttonColors.GREY}
                                onClick={handleSubmit}
                                text={String(finishModalTitle)}
                            />
                        </div>
                    </div>
                </Modal>
                {questionnaireType === SignDocTypes.SURVEY ? (
                    <SurveyInstructionModal
                        open={showInstructionModal}
                        sessionQuestions={sessionQuestions}
                        survey={questionnaireSession}
                        questionsLoaded={questionsLoaded}
                        onStartSurvey={handleStartSession}
                    />
                ) : (<QuestionnaireInstructionModal
                    open={showInstructionModal}
                    sessionQuestions={sessionQuestions}
                    questionnaireSession={questionnaireSession}
                    questionsLoaded={questionsLoaded}
                    onStartExam={handleStartSession}
                />)}

                <QuestionnaireSuccessModal
                    open={showSuccessModal}
                    showConfetti={showConfetti}
                    onClose={closeSuccessModal}
                    onGoHome={closeSuccessModal}
                    questionnaireType={questionnaireType}
                />

                <QuestionnaireFailureModal
                    open={showFailureModal}
                    onClose={closeFailureModal}
                    onGoHome={closeFailureModal}
                />
            </div>
        </div>
    );
};

export const QuestionnaireSessionPageWrapped = () => {
    return (
        <QuestionnaireSessionReactContextProvider>
            <QuestionnaireSessionPage />
        </QuestionnaireSessionReactContextProvider>
    )
}